import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';

interface Props {
  recruiterId: number;
  onClose: () => void;
  onSave: () => void;
}

const AddOpportunityModal: React.FC<Props> = ({ recruiterId, onClose, onSave }) => {
  const [searchType, setSearchType] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState<any>(null);
  const [searching, setSearching] = useState(false);
  const [poderoso, setPoderoso] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    setSearching(true);
    try {
      const params = new URLSearchParams({
        search: searchTerm,
        searchType: searchType,
        recruiter_id: recruiterId.toString()
      });
      const response = await api.get(`/opportunities?${params}`);
      setSearchResults(response.data.opportunities || []);
      if (response.data.opportunities.length === 0) {
        toast.info('Nenhuma oportunidade encontrada');
      }
    } catch (error) {
      toast.error('Erro ao buscar oportunidade');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectOpportunity = (opportunity: any) => {
    setSelectedOpportunity(opportunity);
    setSearchResults([]);
    setSearchTerm('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedOpportunity) {
      toast.error('Selecione uma oportunidade');
      return;
    }
    
    setLoading(true);

    try {
      await api.post(`/recruiter-list/${recruiterId}/opportunities`, {
        opportunity_id: selectedOpportunity.id,
        poderoso
      });
      toast.success('Oportunidade adicionada com sucesso!');
      onSave();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao adicionar oportunidade');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Adicionar Oportunidade</h2>
          <button onClick={onClose} style={styles.closeBtn} type="button">×</button>
        </div>
        
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.searchSection}>
            <h3 style={styles.sectionTitle}>Buscar Oportunidade</h3>
            <div style={styles.searchForm}>
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                style={styles.select}
              >
                <option value="name">Nome</option>
                <option value="id">ID</option>
              </select>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch(e as any);
                  }
                }}
                placeholder={`Buscar por ${searchType === 'id' ? 'ID' : 'nome'}...`}
                style={styles.searchInput}
              />
              <button
                type="button"
                onClick={handleSearch}
                disabled={searching}
                style={styles.searchBtn}
              >
                {searching ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
            
            {searchResults.length > 0 && (
              <div style={styles.searchResults}>
                {searchResults.map((opp) => (
                  <div
                    key={opp.id}
                    onClick={() => handleSelectOpportunity(opp)}
                    style={styles.searchResultItem}
                  >
                    <strong>{opp.name}</strong>
                    <div style={styles.searchResultDetails}>
                      ID: {opp.id}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {selectedOpportunity && (
              <div style={styles.selectedOpportunity}>
                <strong>Oportunidade Selecionada:</strong>
                <div>{selectedOpportunity.name}</div>
                <div style={styles.searchResultDetails}>
                  ID: {selectedOpportunity.id}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedOpportunity(null)}
                  style={styles.removeBtn}
                >
                  Remover Seleção
                </button>
              </div>
            )}
          </div>

          <h3 style={styles.sectionTitle}>Configurações</h3>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={poderoso}
                onChange={(e) => setPoderoso(e.target.checked)}
              />
              Poderoso
            </label>
          </div>
          
          <div style={styles.buttons}>
            <button
              type="button"
              onClick={onClose}
              style={styles.cancelBtn}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !selectedOpportunity}
              style={{
                ...styles.saveBtn,
                ...(loading || !selectedOpportunity ? styles.disabledBtn : {})
              }}
            >
              {loading ? 'Adicionando...' : 'Adicionar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1002,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem 2rem 0 2rem',
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
    padding: 0,
    lineHeight: 1,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '0 2rem 2rem 2rem',
  },
  searchSection: {
    marginBottom: '1.5rem',
    marginTop: '1rem',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: colors.primary,
    marginTop: '1.5rem',
    marginBottom: '1rem',
    borderBottom: `2px solid ${colors.primary}`,
    paddingBottom: '0.5rem',
  },
  searchForm: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  select: {
    padding: '0.5rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  searchInput: {
    flex: 1,
    padding: '0.5rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  searchBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  searchResults: {
    maxHeight: '200px',
    overflowY: 'auto' as const,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  searchResultItem: {
    padding: '0.75rem',
    borderBottom: `1px solid ${colors.border}`,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  searchResultDetails: {
    fontSize: '0.85rem',
    color: colors.textLight,
    marginTop: '0.25rem',
  },
  selectedOpportunity: {
    padding: '1rem',
    backgroundColor: '#e8f5e9',
    border: `1px solid #4caf50`,
    borderRadius: '4px',
  },
  removeBtn: {
    marginTop: '0.5rem',
    padding: '0.25rem 0.5rem',
    backgroundColor: colors.error,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  checkboxGroup: {
    marginBottom: '1rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: colors.text,
    cursor: 'pointer',
    fontSize: '1rem',
  },
  buttons: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  cancelBtn: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.border,
    color: colors.text,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  saveBtn: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  disabledBtn: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default AddOpportunityModal;
